import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { ShieldCheck } from 'lucide-react';

// Certification data. Add, remove or renew entries here — the cards render from this array.
export const CERTIFICATIONS = [
  {
    id: 'iso-9001',
    title: 'ISO 9001',
    subtitle: 'Quality Management',
    description:
      'Demonstrates our commitment to consistent quality, effective processes and continual improvement.',
    image: '/images/certifications/iso-9001.png',
    alt: 'ISO 9001 Quality Management certification',
  },
  {
    id: 'iso-20000-1',
    title: 'ISO/IEC 20000-1',
    subtitle: 'IT Service Management',
    description:
      'Supports structured, reliable and continuously improving IT service management and delivery.',
    image: '/images/certifications/iso-20000-1.png',
    alt: 'ISO/IEC 20000-1 IT Service Management certification',
  },
  {
    id: 'iso-27001',
    title: 'ISO/IEC 27001',
    subtitle: 'Information Security Management',
    description:
      'Demonstrates a systematic approach to managing information security risks and protecting information assets.',
    image: '/images/certifications/iso-27001.png',
    alt: 'ISO/IEC 27001 Information Security Management certification',
  },
  {
    id: 'soc-2-type-ii',
    title: 'SOC 2 Type II',
    subtitle: 'Security & Controls Assurance',
    description:
      'Provides independent assurance regarding the design and operating effectiveness of relevant organizational controls over a defined review period.',
    image: '/images/certifications/soc-2-type-ii.png',
    alt: 'SOC 2 Type II compliance assurance',
  },
];

// Falls back to the certification name if the badge image is unavailable.
const CertificationBadge = ({ src, alt, label }) => {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div
        className="h-24 w-24 rounded-full border-2 border-purple-200 bg-purple-50 flex items-center justify-center px-2"
        role="img"
        aria-label={alt}
      >
        <span className="text-xs font-semibold text-purple-900 text-center leading-tight">{label}</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      width={96}
      height={96}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className="h-24 w-24 object-contain"
    />
  );
};

const ComplianceSection = ({
  eyebrow = 'Trusted standards. Stronger together.',
  title = 'Security, Quality & Compliance',
  description = "Our organization maintains internationally recognized certifications supporting the secure and reliable delivery of our products and services, covering quality management, information security and IT service management.",
  certifications = CERTIFICATIONS,
  className = '',
}) => (
  <section aria-labelledby="compliance-heading" className={`container mx-auto px-4 py-20 ${className}`}>
    <div className="text-center mb-16">
      <p className="text-sm font-semibold tracking-widest uppercase text-purple-600 mb-4">{eyebrow}</p>
      <h2 id="compliance-heading" className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
        {title}
      </h2>
      <p className="text-lg text-gray-600 max-w-2xl mx-auto">{description}</p>
    </div>

    <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 list-none p-0 m-0">
      {certifications.map((cert) => (
        <li key={cert.id} className="flex">
          <Card className="w-full border-none shadow-lg hover:shadow-2xl transition-all hover:scale-105 motion-reduce:transform-none motion-reduce:transition-none bg-gradient-to-br from-white to-purple-50">
            <CardContent className="pt-6 flex flex-col items-center text-center">
              <CertificationBadge src={cert.image} alt={cert.alt} label={cert.title} />
              <h3 className="text-xl font-semibold mt-6 mb-2 text-purple-900">{cert.title}</h3>
              <p className="text-xs font-semibold tracking-wider uppercase text-gray-500 mb-4">
                {cert.subtitle}
              </p>
              <p className="text-sm text-gray-700">{cert.description}</p>
            </CardContent>
          </Card>
        </li>
      ))}
    </ul>

    <div className="mt-12 max-w-3xl mx-auto flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
      <ShieldCheck className="text-purple-600 shrink-0" size={32} aria-hidden="true" />
      <p className="text-sm text-gray-600">
        Independently audited standards for quality management, IT service management and information
        security, supporting secure and reliable delivery of the VivyaSigns platform.
      </p>
    </div>
  </section>
);

// Compact badge for the footer strip; omitted entirely if the image is unavailable.
const FooterBadge = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);

  if (failed) return null;

  return (
    <img
      src={src}
      alt={alt}
      width={40}
      height={40}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className="h-10 w-10 object-contain bg-white rounded-md p-1"
    />
  );
};

// Compact footer variant.
export const ComplianceBadgeStrip = ({ certifications = CERTIFICATIONS }) => (
  <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
    <ul className="flex flex-wrap items-center justify-center gap-4 list-none p-0 m-0">
      {certifications.map((cert) => (
        <li key={cert.id}>
          <FooterBadge src={cert.image} alt={cert.alt} />
        </li>
      ))}
    </ul>
    <p className="text-xs text-gray-400 text-center">
      Quality &bull; Security &bull; IT Service Management &bull; Independent Assurance
    </p>
  </div>
);

export default ComplianceSection;
